<script lang="ts">
  import { isNonEmptyArray, isNonEmptyString } from '#app/lib/boolean_tests'
  import { imgSrc } from '#app/lib/image_source'
  import type { EntityAutocompleteSuggestion } from '#entities/components/entity_autocomplete_selector.svelte'
  import { getEntityImagePath } from '#entities/lib/entities'
  import { entityTypeNameByType } from '#entities/lib/types/entities_types'
  import type { PropertyUri } from '#server/types/entity'
  import { I18n } from '#user/lib/i18n'

  const {
    suggestion,
    highlight = false,
    displaySuggestionType = false,
    scrollableElement,
    displayedProperty,
    onSelect,
  }: {
    suggestion: EntityAutocompleteSuggestion
    highlight?: boolean
    displaySuggestionType?: boolean
    scrollableElement?: HTMLElement
    displayedProperty?: PropertyUri
    onSelect: (suggestion: EntityAutocompleteSuggestion) => void
  } = $props()

  let element: HTMLElement = $state()
  const uri = $derived(suggestion.uri)
  const type = $derived(suggestion.type)
  const label = $derived(suggestion.label)
  const description = $derived(suggestion.description)
  const images = $derived(suggestion.image)
  const claims = $derived(suggestion.claims)
  const typeName = $derived(entityTypeNameByType[type])

  const image = $derived.by(() => {
    if (isNonEmptyArray(images)) {
      return images?.[0]
    } else if (isNonEmptyString(images)) {
      // Known case: when searching for a language entity
      return images
    }
  })
  const imageUrl = $derived(image ? getEntityImagePath(image) : null)

  $effect(() => {
    if (element && highlight && scrollableElement) {
      scrollableElement.scroll({
        top: Math.max(0, (element.offsetTop - 20)),
        behavior: 'smooth',
      })
    }
  })

  function _onSelect (e) {
    e.stopPropagation()
    onSelect(suggestion)
  }
</script>

<li bind:this={element}>
  <button
    onclick={_onSelect}
    class:highlight
  >
    <div
      class="image"
      style:background-image={imageUrl ? `url(${imgSrc(imageUrl, 90)})` : ''}
    ></div>
    <div class="right">
      <div class="top">
        <span class="label">{label}</span>
        {#if displayedProperty && claims[displayedProperty][0]}
          <span class="claim">{claims[displayedProperty][0]}</span>
        {/if}
        <a
          class="uri"
          href="/entity/{uri}"
          target="_blank"
          rel="noreferrer"
          onclick={e => e.stopPropagation()}
        >{uri}</a>
      </div>
      <div class="bottom">
        {#if description}<span class="description">{description}</span>{/if}
        {#if displaySuggestionType}<span class="type">{I18n(typeName)}</span>{/if}
      </div>
    </div>
  </button>
</li>

<style lang="scss">
  @import "general/scss/utils";
  li{
    margin: 0;
  }
  button{
    @include bg-hover(white);
    inline-size: 100%;
    margin: 0;
    padding: 0;
    @include display-flex(row, stretch);
  }
  .highlight{
    background-color: #ddd;
  }
  .image{
    margin-inline-end: 0.3em;
    inline-size: 48px;
    block-size: 48px;
    background-size: cover;
    background-position: center center;
  }
  .right{
    padding: 0.5em;
    flex: 1 0 0;
  }
  .top{
    @include display-flex(row, center, space-between);
  }
  .bottom{
    @include display-flex(row, center, space-between);
  }
  .label{
    font-weight: bold;
    text-align: start;
    margin-inline-end: auto;
  }
  .description, .uri{
    font-weight: normal;
  }
  .type, .description{
    text-align: start;
  }
  .description{
    color: $grey;
    display: block;
  }
  .type, .claim{
    color: $grey;
    font-size: 0.9rem;
  }
  .type{
    text-align: end;
  }
  .uri{
    white-space: nowrap;
    align-self: flex-start;
  }
</style>
